import { TabContext, TabList } from '@mui/lab';
import {
	Box,
	debounce,
	FormControl,
	Grid,
	InputAdornment,
	OutlinedInput,
	Stack,
	styled,
	Tab,
	Typography,
} from '@mui/material';

import {
	useEffect,
	useState,
	SyntheticEvent,
	useRef,
	useCallback,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';

import { circleDeleteWhiteIcon } from 'common/assets/images';
import { ReactComponent as Comment } from 'common/assets/images/comment.svg';
import { ReactComponent as HeaderIcon } from 'common/assets/images/reg-table-landing-icon.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { ReactComponent as UploadIcon } from 'common/assets/images/upload-light.svg';
import { IAdvancedFilterListItemOptions } from 'common/components/Filters/AdvancedFilterListItems';
import AdvancedFilters, {
	IAdvancedFiltersOptions,
	IAdvancedFilterValueOptions,
} from 'common/components/Filters/AdvancedFilters';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import {
	AI_SIMULATION_REG_SEARCH_TABS,
	COPYANDPASTE_TEXT,
	AI_SIMULATION_TYPES,
} from 'common/constants/AISimulationConstants';
import { FILTER_NAME, FILTER_TYPE } from 'common/constants/CitationDetails';
import { RootState } from 'common/store/AppStore';
import {
	setAdvFilterDrawerState,
	setAppliedfilterKeys,
	setRegulationControlSelectedTab,
	setRegulationTableSearchText,
	setTextFromFile,
} from 'common/store/reducer/simulationReducer';
import {
	filterDataHandler,
	scrollToTop,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { useUploadForm } from 'hooks/useUploadForm';

import { IStyledTabProps } from 'features/HorizonScanning/api/type-def';
import { getFilterItemList } from 'features/RegulationInventory/api';
import { IRegInventoryFilterHeaders } from 'features/RegulationInventory/api/type-def';

import { getAiSimulationTitle } from '../AiSimulationUtil';
import { IAIRegFilterResponse } from '../api/type-def';
import RegulationTable from '../Components/RegulationTable/RegulationTable';
import StepHeader from '../Components/StepHeader/StepHeader';
import {
	ButtonComp,
	HTTP_STATUS_CODES,
} from '../ObligationAndControl/ObligationAndControlModules';
import CopyAndPasteImportModal from '../SimulationArena/CopyAndPasteImportModal';
import CopyAndPasteTab from '../SimulationArena/CopyAndPasteTab';

import styles from './SimulationRegulationGenerate.module.scss';

const { HEADING1, HEADING3 } = COPYANDPASTE_TEXT;
const { OBLIGATION_TO_POLICY_SUMMARY, REGULATION_OBLIGATION_SUMMARY } =
	AI_SIMULATION_TYPES;
const {
	focusGeneratetabColor,
	tabsGenerateTextColor,
	tabGenerateSelectedTextColor,
	whiteGenerateTextColor,
	selectedGenerateTabBackground,
	tabGenerateBorderRadius,
	tabGenerateBackground,
	commentGenerateIconColor,
} = styles;

const style = {
	'.MuiButtonBase-root': {
		backgroundColor: tabGenerateBackground,
		border: 'none',
	},
	'.MuiButtonBase-root.MuiTab-root.Mui-selected': {
		color: tabGenerateSelectedTextColor,
	},
	'.Mui-selected': {
		background: `${focusGeneratetabColor} !important`,
		color: tabsGenerateTextColor,
		borderRadius: tabGenerateBorderRadius,
	},
	'.MuiTabs-flexContainer': {
		backgroundColor: `${tabGenerateBackground} !important`,
	},
	'.MuiTabs-indicator': {
		display: 'none',
	},
};

const StyledTab = styled((props: IStyledTabProps) => (
	<Tab disableRipple {...props} />
))({
	textTransform: 'none',
	fontSize: '16px',
	color: tabsGenerateTextColor,
	borderRadius: tabGenerateBorderRadius,
	padding: '4px 12px',
	'&.Mui-selected': {
		color: whiteGenerateTextColor,
		background: selectedGenerateTabBackground,
	},
	'& .MuiTabs-indicator': {
		backgroundColor: selectedGenerateTabBackground,
	},
	'&.Mui-focusVisible': {
		backgroundColor: selectedGenerateTabBackground,
	},
});

const { COPY, SEARCH } = AI_SIMULATION_REG_SEARCH_TABS;
const { OK } = HTTP_STATUS_CODES;
const { imageColor } = styles;

const { THEME_TYPE, REG_SECTION_TYPE, JURISDICTION_TYPE, ISSUANCE_TYPE } =
	FILTER_TYPE;

const { THEME_NAME, REG_SECTION_NAME, JURISDICTION_NAME, ISSUANCE_TYPE_NAME } =
	FILTER_NAME;

interface ISimulationFilterElements {
	themeName: ISimulationFilterComps;
	sectionName: ISimulationFilterComps;
	juridiction: ISimulationFilterComps;
	issuanceType: ISimulationFilterComps;
}

interface ISimulationFilterComps {
	type: string;
	name: string;
	loading: boolean;
	data: IAdvancedFilterListItemOptions[];
	searchText: string;
	searchFlag: boolean;
}

const defaultOptionsList: ISimulationFilterElements = {
	themeName: {
		type: THEME_TYPE,
		name: THEME_NAME,
		loading: false,
		data: [],
		searchText: '',
		searchFlag: false,
	},
	sectionName: {
		type: REG_SECTION_TYPE,
		name: REG_SECTION_NAME,
		loading: false,
		data: [],
		searchText: '',
		searchFlag: false,
	},
	juridiction: {
		type: JURISDICTION_TYPE,
		name: JURISDICTION_NAME,
		loading: false,
		data: [],
		searchText: '',
		searchFlag: false,
	},
	issuanceType: {
		type: ISSUANCE_TYPE,
		name: ISSUANCE_TYPE_NAME,
		loading: false,
		data: [],
		searchText: '',
		searchFlag: false,
	},
};

const SimulationRegulationGenerate = () => {
	const { simulationType } = useParams();
	const dispatch = useDispatch();

	// parameters for search text
	const regulationTableSearchText = useSelector(
		(state: RootState) => state.simulationArena.regulationTableSearchText,
	);

	// parameters for adv filter
	const advFilterDrawerState = useSelector(
		(state: RootState) => state.simulationArena.advFilterDrawerState,
	);

	const appliedfilterKeys = useSelector(
		(state: RootState) => state.simulationArena.appliedfilterKeys,
	);

	const regulationControlSelectedTab = useSelector(
		(state: RootState) => state.simulationArena.regulationControlSelectedTab,
	);

	const [tabValue, setTabValue] = useState<string>(
		regulationControlSelectedTab,
	);
	const searchvalueRef = useRef<any>(null);
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [loading, setIsLoading] = useState<boolean>(false);

	const [options, setOptions] =
		useState<ISimulationFilterElements>(defaultOptionsList);

	const title = getAiSimulationTitle(simulationType ?? '');

	const textFromFile = useSelector(
		(state: RootState) => state.simulationArena.textFromFile,
	);

	const selectedOptions: IAdvancedFilterValueOptions[] =
		appliedfilterKeys.flatMap((res: any) => {
			const subOptions: IAdvancedFilterValueOptions = res.values?.map(
				(ele: any) => ({
					label: ele,
					value: ele,
					checked: true,
					type: res.key,
				}),
			);
			return subOptions;
		});

	const menuItems: IAdvancedFiltersOptions[] = [
		{
			filterKey: options.themeName.type,
			selectedOptions: selectedOptions.filter((res) => res.type === THEME_TYPE),
			filterValues: options.themeName.data,
			filterTitle: options.themeName.name,
			isLoading: options.themeName.loading,
			searchText: options.themeName.searchText,
			searchFlag: options.themeName.searchFlag,
		},
		{
			filterKey: options.sectionName.type,
			selectedOptions: selectedOptions.filter(
				(res) => res.type === REG_SECTION_TYPE,
			),
			filterValues: options.sectionName.data,
			filterTitle: options.sectionName.name,
			isLoading: options.sectionName.loading,
			searchText: options.sectionName.searchText,
			searchFlag: options.sectionName.searchFlag,
		},
		{
			filterKey: options.juridiction.type,
			selectedOptions: selectedOptions.filter(
				(res) => res.type === JURISDICTION_TYPE,
			),
			filterValues: options.juridiction.data,
			filterTitle: options.juridiction.name,
			isLoading: options.juridiction.loading,
			searchText: options.juridiction.searchText,
			searchFlag: options.juridiction.searchFlag,
		},
		{
			filterKey: options.issuanceType.type,
			selectedOptions: selectedOptions.filter(
				(res) => res.type === ISSUANCE_TYPE,
			),
			filterValues: options.issuanceType.data,
			filterTitle: options.issuanceType.type,
			isLoading: options.issuanceType.loading,
			searchText: options.issuanceType.searchText,
			searchFlag: options.issuanceType.searchFlag,
		},
	];

	const handleTabChange = (event: SyntheticEvent, newValue: string) => {
		setTabValue(newValue);
		dispatch(setRegulationControlSelectedTab(newValue));
	};

	const changeSearchInput = (event: any) => {
		const text = event?.target?.value;
		dispatch(setRegulationTableSearchText(text));
	};

	const handleClearSearchText = () => {
		dispatch(setRegulationTableSearchText(''));
		searchvalueRef.current.value = '';
	};

	const debouncedSearchHandler = debounce(changeSearchInput, 500);

	const onCloseAdvancedFilter = () => {
		setOptions(defaultOptionsList);
		dispatch(setAdvFilterDrawerState(!advFilterDrawerState));
	};

	useEffect(() => {
		scrollToTop();
	}, []);

	const handleClose = () => {
		setOpenModal(false);
	};

	const { uploadForm, uploadResponse, uploadError } = useUploadForm(
		`${process.env.REACT_APP_FILE_UPLOAD_SERVICE_URL}/convert`,
	);

	const handleAcceptSubmit = useCallback(
		async (data: File[]) => {
			const fd = new FormData();
			fd.append('file', data[0]);
			setIsLoading(true);
			await uploadForm(fd);
		},
		[uploadResponse],
	);

	const handleUploadButtonClick = () => {
		setOpenModal(true);
	};

	useEffect(() => {
		if (uploadResponse) {
			dispatch(setTextFromFile(uploadResponse.data.text));
			setTabValue(COPY);
		} else {
			dispatch(setTextFromFile(''));
		}
		setIsLoading(false);
	}, [uploadResponse]);

	useEffect(() => {
		if (uploadError) {
			setIsLoading(false);
		}
	}, [uploadError]);

	useEffect(() => {
		handleClose();
	}, [textFromFile]);

	const clearAllButtonClicked = () => {
		dispatch(setAppliedfilterKeys([]));
	};

	const cancelClicked = () => {
		onCloseAdvancedFilter();
	};

	const advancedFilterApplyClicked = (data: IAdvancedFilterValueOptions[]) => {
		const filters = filterDataHandler(data, false, null);
		dispatch(setAppliedfilterKeys(filters));
	};

	useEffect(() => {
		if (appliedfilterKeys.length > 0) {
			onCloseAdvancedFilter();
		}
	}, [appliedfilterKeys]);

	const handleFilterEmptyResponse = (type: string, searchFlag: boolean) => {
		switch (type) {
			case THEME_TYPE:
				setOptions((prev) => ({
					...prev,
					themeName: { ...prev.themeName, data: [], searchFlag },
				}));
				break;
			case REG_SECTION_TYPE:
				setOptions((prev) => ({
					...prev,
					sectionName: { ...prev.sectionName, data: [], searchFlag },
				}));
				break;
			case JURISDICTION_TYPE:
				setOptions((prev) => ({
					...prev,
					juridiction: { ...prev.juridiction, data: [], searchFlag },
				}));
				break;
			case ISSUANCE_TYPE:
				setOptions((prev) => ({
					...prev,
					issuanceType: { ...prev.issuanceType, data: [], searchFlag },
				}));
				break;
			default:
				break;
		}
	};

	const setFilterOptions = (response: any, name: string, type: string) => {
		handleFilterEmptyResponse(type, true);
		response.forEach((item: IAIRegFilterResponse) => {
			if (item.themeName) {
				const themeObject = {
					value: name,
					label: item.themeName,
					type,
					checked: false,
				};
				setOptions((prev) => ({
					...prev,
					themeName: {
						...prev.themeName,
						data: [...prev.themeName.data, themeObject],
					},
				}));
			} else if (item.sectionName) {
				const sectionObject = {
					value: name,
					label: item.sectionName,
					type,
				};
				setOptions((prev) => ({
					...prev,
					sectionName: {
						...prev.sectionName,
						data: [...prev.sectionName.data, sectionObject],
					},
				}));
			} else if (item.jurisdiction || item.unitedStates || item.international) {
				const label =
					item.jurisdiction || item.unitedStates || item.international || '';
				const jurisdictionObject = {
					value: name,
					label,
					type,
				};
				setOptions((prev) => ({
					...prev,
					juridiction: {
						...prev.juridiction,
						data: [...prev.juridiction.data, jurisdictionObject],
					},
				}));
			} else if (item.issuanceType) {
				const issuanceTypeObject = {
					value: name,
					label: item.issuanceType,
					type,
				};
				setOptions((prev) => ({
					...prev,
					issuanceType: {
						...prev.issuanceType,
						data: [...prev.issuanceType.data, issuanceTypeObject],
					},
				}));
			}
		});
	};

	const showLoader = (type: string, loading: boolean) => {
		switch (type) {
			case THEME_TYPE:
				setOptions((prev) => ({
					...prev,
					themeName: { ...prev.themeName, loading },
				}));
				break;
			case REG_SECTION_TYPE:
				setOptions((prev) => ({
					...prev,
					sectionName: { ...prev.sectionName, loading },
				}));
				break;
			case JURISDICTION_TYPE:
				setOptions((prev) => ({
					...prev,
					juridiction: { ...prev.juridiction, loading },
				}));
				break;
			case ISSUANCE_TYPE:
				setOptions((prev) => ({
					...prev,
					issuanceType: { ...prev.issuanceType, loading },
				}));
				break;
			default:
				break;
		}
	};

	const setSearchFieldText = (type: string, searchText: string) => {
		switch (type) {
			case THEME_TYPE:
				setOptions((prev) => ({
					...prev,
					themeName: { ...prev.themeName, searchText },
				}));
				break;
			case REG_SECTION_TYPE:
				setOptions((prev) => ({
					...prev,
					sectionName: { ...prev.sectionName, searchText },
				}));
				break;
			case JURISDICTION_TYPE:
				setOptions((prev) => ({
					...prev,
					juridiction: { ...prev.juridiction, searchText },
				}));
				break;
			case ISSUANCE_TYPE:
				setOptions((prev) => ({
					...prev,
					issuanceType: { ...prev.issuanceType, searchText },
				}));
				break;
			default:
				break;
		}
	};

	const onSearchInputChange = async (
		searchString: string,
		type: string,
		name: string,
	) => {
		setSearchFieldText(type, searchString);
		if (searchString.length < 3) {
			handleFilterEmptyResponse(type, false);
			return;
		}
		const headers: IRegInventoryFilterHeaders = {
			searchField: [type],
			searchText: searchString,
			index: `reginv-theme-index-${process.env.REACT_APP_ENVIRONMENT}`,
		};
		showLoader(type, true);
		const response: any = await getFilterItemList(headers);
		if (response?.statusCode === OK) {
			showLoader(type, false);
			setFilterOptions(response?.responseBody, name, type);
		} else {
			handleFilterEmptyResponse(type, true);
			showLoader(type, false);
		}
	};

	const getUploadBtnStatus = () =>
		simulationType?.toLowerCase() ===
			OBLIGATION_TO_POLICY_SUMMARY.toLowerCase() ||
		simulationType?.toLowerCase() ===
			REGULATION_OBLIGATION_SUMMARY.toLowerCase();
	return (
		<>
			<StepHeader title={title} Icon={HeaderIcon} />
			<Grid container alignItems="center" justifyContent="space-between" mb={1}>
				<Grid item display="flex" alignItems="center">
					<TabContext value={tabValue}>
						<TabList
							sx={style}
							onChange={handleTabChange}
							className={styles['panel-layout']}
						>
							<StyledTab
								label={SEARCH}
								value={SEARCH}
								data-testid="search-tab"
							/>
							<StyledTab label={COPY} value={COPY} data-testid="copy-tab" />
						</TabList>
					</TabContext>
				</Grid>
				{getUploadBtnStatus() && (
					<Grid item display="flex" alignItems="flex-end">
						<ButtonComp
							btnType="secondary"
							onClick={handleUploadButtonClick}
							startIcon={
								<Box className={styles['upload-button-icon']}>
									<UploadIcon fill={imageColor} />
								</Box>
							}
						>
							Upload
						</ButtonComp>
					</Grid>
				)}
			</Grid>
			{tabValue === SEARCH && (
				<Grid container className={styles['top-header']}>
					<Stack direction="column" width="100%">
						<Stack direction="row" alignItems="center" spacing={1}>
							<Comment
								fill={commentGenerateIconColor}
								className={styles['copy-icon']}
							/>
							<Typography className={styles['copy-paste-heading1']}>
								{HEADING1}
							</Typography>
						</Stack>
						<Typography className={styles['copy-paste-heading2']}>
							{HEADING3}
						</Typography>
						<FormControl
							variant="standard"
							className={styles['reg-table-search-input']}
						>
							<OutlinedInput
								className={styles['detail-summary-bg']}
								name={regulationTableSearchText}
								defaultValue={regulationTableSearchText}
								inputRef={searchvalueRef}
								inputProps={{ 'data-testid': 'search-box' }}
								onChange={debouncedSearchHandler}
								id="input-with-icon-adornment"
								placeholder={toSentenceCase('Search Database')}
								startAdornment={
									<InputAdornment position="start">
										<SearchOutlined className={styles['input-search-icon']} />
									</InputAdornment>
								}
								endAdornment={
									regulationTableSearchText && (
										<InputAdornment position="end">
											<button
												type="button"
												tabIndex={0}
												data-testid="clear-icon"
												onClick={handleClearSearchText}
												onKeyDown={handleClearSearchText}
												className={styles['cursor-pointer']}
												aria-label="Clear"
											>
												<img src={circleDeleteWhiteIcon} alt="circle-icon" />
											</button>
										</InputAdornment>
									)
								}
							/>
						</FormControl>
					</Stack>
				</Grid>
			)}
			{tabValue === COPY && <CopyAndPasteTab />}
			{tabValue === SEARCH && (
				<Stack>
					<RegulationTable
						type={simulationType || ''}
						appliedfilterKeys={appliedfilterKeys}
					/>
				</Stack>
			)}
			{advFilterDrawerState && (
				<SideDrawer
					className={styles['simulation-reg-advancefilter-drawer']}
					drawerState={advFilterDrawerState}
					customClass={styles['custom-width']}
				>
					<AdvancedFilters
						applyFiltersHandler={advancedFilterApplyClicked}
						handleClearFilter={clearAllButtonClicked}
						onClose={onCloseAdvancedFilter}
						filterOptionsData={menuItems}
						filterDataLoading={false}
						selectedListOptions={selectedOptions}
						onCancel={cancelClicked}
						onSearchInputChange={onSearchInputChange}
						moduleType={false}
					/>
				</SideDrawer>
			)}
			<CopyAndPasteImportModal
				openModal={openModal}
				handleClose={handleClose}
				isLoading={loading}
				isError={uploadError}
				handleSubmit={handleAcceptSubmit}
			/>
		</>
	);
};

export default SimulationRegulationGenerate;
